import { Head } from '@inertiajs/react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import SyntaxHighlighter from 'react-syntax-highlighter'
import { atomOneDark } from 'react-syntax-highlighter/dist/esm/styles/hljs'
import { Files, AlertCircle, CheckCircle, RefreshCw } from "lucide-react"
import singleFileWidgetImage from '/assets/single_uploader.png'
import multiFileWidgetImage from '/assets/multi-file-uploader.png'

export default function LimitNumberOfFiles() {
  return (
    <div className="container mx-auto py-6 px-4 max-w-6xl">
      <Head title="Limit Number of Files - Documentation" />
      
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2">Limit Number of Files</h1>
        <p className="text-lg text-gray-600 mb-4">
          Control the maximum number of files that can be uploaded through a single widget using the <code className="bg-gray-100 px-2 py-1 rounded">max-files</code> attribute.
        </p>
        
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <Files className="h-5 w-5 text-blue-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                <strong>Default Behavior:</strong> If no <code className="bg-blue-100 px-1 py-0.5 rounded text-blue-800">max-files</code> attribute is specified, the widget defaults to a maximum of 5 files.
              </p>
            </div>
          </div>
        </div>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Basic Usage</CardTitle>
          <CardDescription>
            Set the maximum number of files that can be uploaded through a widget
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-2">Single File Upload</h3>
            <p className="text-gray-600 mb-4">
              For single file uploads, you have two options: use <code className="bg-gray-100 px-2 py-1 rounded">multiple="false"</code> for a smaller widget interface, or use <code className="bg-gray-100 px-2 py-1 rounded">max-files="1"</code> to limit the standard widget to one file.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold mb-2 text-blue-600">Option 1: Single File Widget</h4>
                <p className="text-sm text-gray-600 mb-3">
                  Use <code className="bg-gray-100 px-1 py-0.5 rounded">multiple="false"</code> for single file uploads:
                </p>
                <SyntaxHighlighter
                  language="xml"
                  style={atomOneDark}
                  customStyle={{
                    background: '#21252b',
                    padding: '1rem',
                    margin: 0,
                    fontSize: '.8rem',
                    borderRadius: '6px'
                  }}
                >
                  {`<simple-file-upload 
  public-key="your-key"
  multiple="false"
></simple-file-upload>`}
                </SyntaxHighlighter>
                
                <img src={singleFileWidgetImage} alt="Single file widget interface" className="w-full h-auto mt-4" />
              </div>

              <div className="border rounded-lg p-4">
                <h4 className="font-semibold mb-2 text-green-600">Option 2: Limited Multi-File Widget</h4>
                <p className="text-sm text-gray-600 mb-3">
                  Use <code className="bg-gray-100 px-1 py-0.5 rounded">max-files="1"</code> to limit the standard widget:
                </p>
                <SyntaxHighlighter
                  language="xml"
                  style={atomOneDark}
                  customStyle={{
                    background: '#21252b',
                    padding: '1rem',
                    margin: 0,
                    fontSize: '.8rem',
                    borderRadius: '6px'
                  }}
                >
                  {`<simple-file-upload 
  public-key="your-key"
  max-files="1"
></simple-file-upload>`}  
                </SyntaxHighlighter>
                
                <img src={multiFileWidgetImage} alt="Standard widget limited to 1 file" className="w-full h-auto mt-4" />
              </div>
            </div>

          </div>
          <div>
            <h3 className="text-lg font-semibold mb-2">Multiple Files with Custom Limit</h3>
            <p className="text-gray-600 mb-3">
              Allow up to a specific number of files:
            </p>
            <SyntaxHighlighter
              language="xml"
              style={atomOneDark}
              customStyle={{
                background: '#21252b',
                padding: '1rem',
                margin: 0,
                fontSize: '.8rem',
                borderRadius: '6px'
              }}
            >
              {`<simple-file-upload 
  public-key="your-key"
  max-files="3"
></simple-file-upload>`}
            </SyntaxHighlighter>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-2">Default Behavior (No Limit Specified)</h3>
            <p className="text-gray-600 mb-3">
              When no <code className="bg-gray-100 px-2 py-1 rounded">max-files</code> attribute is provided, the widget defaults to 5 files:
            </p>
            <SyntaxHighlighter
              language="xml"
              style={atomOneDark}
              customStyle={{
                background: '#21252b',
                padding: '1rem',
                margin: 0,
                fontSize: '.8rem',
                borderRadius: '6px'
              }}
            >
              {`<!-- This widget will accept up to 5 files by default -->
<simple-file-upload 
  public-key="your-key"
></simple-file-upload>`}
            </SyntaxHighlighter>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Persistent State Behavior</CardTitle>
          <CardDescription>
            How the widget maintains file count across user interactions
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start">
              <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5" />
              <div>
                <h4 className="font-medium text-green-800 mb-1">State Persistence</h4>
                <p className="text-sm text-green-700">
                  The widget maintains the count of uploaded files even when the upload dialog is closed and reopened. For example, if <code className="bg-green-100 px-1 py-0.5 rounded">max-files="2"</code>, a user can upload 1 file, close the widget, then return later to upload the remaining 1 file.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-start">
              <RefreshCw className="w-5 h-5 text-orange-500 mr-2 mt-0.5" />
              <div>
                <h4 className="font-medium text-orange-800 mb-1">State Reset</h4>
                <p className="text-sm text-orange-700">
                  The widget's file count resets to zero when the page is fully reloaded. This allows users to start fresh with their file upload quota. If you need to reset the page earlier, you use uploader.reset() (details below)
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Programmatic Reset</CardTitle>
          <CardDescription>
            Reset the uploader state and file count using JavaScript
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <p className="text-gray-600 mb-4">
              You can programmatically reset the uploader to clear all uploaded files and return the file count to zero. This is useful for providing users with a "start over" option without requiring a page reload.
            </p>
            
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
              <div className="flex items-start">
                <RefreshCw className="w-5 h-5 text-purple-500 mr-2 mt-0.5" />
                <div>
                  <h4 className="font-medium text-purple-800 mb-1">Reset Method</h4>
                  <p className="text-sm text-purple-700">
                    The <code className="bg-purple-100 px-1 py-0.5 rounded">reset()</code> method completely removes all file details and resets the uploader to its default state.
                  </p>
                </div>
              </div>
            </div>

            <h3 className="text-lg font-semibold mb-2">Basic Reset Example</h3>
            <p className="text-gray-600 mb-3">
              Get a reference to the uploader and call the reset method:
            </p>
            <SyntaxHighlighter
              language="javascript"
              style={atomOneDark}
              customStyle={{
                background: '#21252b',
                padding: '1rem',
                margin: 0,
                fontSize: '.8rem',
                borderRadius: '6px'
              }}
            >
              {`// Get reference to the component
const uploader = document.querySelector('simple-file-upload');

// Call the reset method
uploader.reset();`}
            </SyntaxHighlighter>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-2">Complete Interactive Example</h3>
            <p className="text-gray-600 mb-3">
              Here's a complete example with a reset button:
            </p>
            <SyntaxHighlighter
              language="html"
              style={atomOneDark}
              customStyle={{
                background: '#21252b',
                padding: '1rem',
                margin: 0,
                fontSize: '.8rem',
                borderRadius: '6px'
              }}
            >
              {`<simple-file-upload 
  id="avatar-uploader"
  public-key="your-key"
  max-files="3"
  tag="avatar">
</simple-file-upload>

<button id="reset-btn">Reset Uploader</button>

<script>
  document.getElementById('reset-btn').addEventListener('click', function() {
    const uploader = document.getElementById('avatar-uploader');
    uploader.reset();
    console.log('Uploader has been reset');
  });
</script>`}
            </SyntaxHighlighter>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Common Use Cases</CardTitle>
          <CardDescription>
            Practical examples for different scenarios
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="border rounded-lg p-4">
              <h4 className="font-semibold mb-2 text-blue-600">Profile Photo Upload</h4>
              <p className="text-sm text-gray-600 mb-3">Single image for user avatars</p>
              <SyntaxHighlighter
                language="html"
                style={atomOneDark}
                customStyle={{
                  background: '#21252b',
                  padding: '1rem',
                  margin: 0,
                  fontSize: '.8rem',
                  borderRadius: '6px'
                }}
              >
                {`<simple-file-upload 
  public-key="your-key"
  max-files="1"
  multiple="false"
  tag="profile-photo"
></simple-file-upload>`}
              </SyntaxHighlighter>
            </div>

            <div className="border rounded-lg p-4">
              <h4 className="font-semibold mb-2 text-green-600">Product Gallery</h4>
              <p className="text-sm text-gray-600 mb-3">Multiple product images</p>
              <SyntaxHighlighter
                language="html"
                style={atomOneDark}
                customStyle={{
                  background: '#21252b',
                  padding: '1rem',
                  margin: 0,
                  fontSize: '.8rem',
                  borderRadius: '6px'
                }}
              >
                {`<simple-file-upload 
  public-key="your-key"
  max-files="10"
  tag="product-images"
></simple-file-upload>`}
              </SyntaxHighlighter>
            </div>

            <div className="border rounded-lg p-4">
              <h4 className="font-semibold mb-2 text-purple-600">Document Upload</h4>
              <p className="text-sm text-gray-600 mb-3">Limited document collection</p>
              <SyntaxHighlighter
                language="html"
                style={atomOneDark}
                customStyle={{
                  background: '#21252b',
                  padding: '1rem',
                  margin: 0,
                  fontSize: '.8rem',
                  borderRadius: '6px'
                }}
              >
                {`<simple-file-upload 
  public-key="your-key"
  max-files="3"
  tag="documents"
></simple-file-upload>`}
              </SyntaxHighlighter>
            </div>

            <div className="border rounded-lg p-4">
              <h4 className="font-semibold mb-2 text-orange-600">Default Behavior</h4>
              <p className="text-sm text-gray-600 mb-3">General file uploads (5 files max)</p>
              <SyntaxHighlighter
                language="html"
                style={atomOneDark}
                customStyle={{
                  background: '#21252b',
                  padding: '1rem',
                  margin: 0,
                  fontSize: '.8rem',
                  borderRadius: '6px'
                }}
              >
                {`<simple-file-upload 
  public-key="your-key"
  tag="general"
></simple-file-upload>`}
              </SyntaxHighlighter>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Best Practices</CardTitle>
          <CardDescription>
            Recommendations for setting file limits
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-green-600 mb-2">✅ Recommended Practices</h4>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">•</span>
                    <span>Set <code className="bg-gray-100 px-1 py-0.5 rounded">max-files="1"</code> for profile photos and avatars</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">•</span>
                    <span>Use reasonable limits (3-10) for product galleries</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">•</span>
                    <span>Consider your storage and bandwidth constraints</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">•</span>
                    <span>Inform users about file limits in your UI</span>
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-red-600 mb-2">❌ Avoid These</h4>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2">•</span>
                    <span>Setting extremely high limits </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2">•</span>
                    <span>Using <code className="bg-gray-100 px-1 py-0.5 rounded">max-files="0"</code> (invalid value)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2">•</span>
                    <span>Not communicating limits to users beforehand</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2">•</span>
                    <span>Forgetting that state persists across widget interactions</span>
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium mb-2">💡 Pro Tips:</h4>
              <ul className="text-sm space-y-1 text-gray-600">
                <li>• The widget will prevent users from selecting more files once the limit is reached</li>
                <li>• Users can remove uploaded files to free up slots for new uploads</li>
                <li>• Consider combining with file size limits for comprehensive upload control</li>
                <li>• Test the user experience with your chosen limits to ensure they're practical</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
